/*
 * Tables as clipboard content and files: an HTML table plus tab-separated
 * text, which Excel and Google Sheets paste as cells, and CSV text. No
 * dependencies, so chat tables can use it without the spreadsheet readers.
 */

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** An HTML table spreadsheets paste as cells (first row bold when a header). */
export function rowsToHtml(rows: string[][], header = true): string {
  const body = rows
    .map((row, r) => {
      const tag = header && r === 0 ? 'th' : 'td';
      return `<tr>${row
        .map((value) => {
          // Codes with leading zeros (ZIPs, IDs) stay text when Excel pastes.
          const text = /^0\d+$/.test(value.trim())
            ? ` style="mso-number-format:'\\@'"`
            : '';
          return `<${tag}${text}>${escapeHtml(value).replace(/\r?\n/g, '<br>')}</${tag}>`;
        })
        .join('')}</tr>`;
    })
    .join('');
  return `<meta charset="utf-8"><table>${body}</table>`;
}

/** Tab-separated text; cells with tabs, quotes or line breaks are quoted. */
export function rowsToTsv(rows: string[][]): string {
  return rows
    .map((row) =>
      row
        .map((value) =>
          /[\t\n\r"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value,
        )
        .join('\t'),
    )
    .join('\n');
}

/**
 * Puts rows on the clipboard as an HTML table and as tab-separated text.
 * The rows may still be loading: Safari only lets a click write the
 * clipboard synchronously, so pending content goes in a ClipboardItem.
 */
export async function copyRows(
  rows: string[][] | Promise<string[][]>,
  header = true,
): Promise<void> {
  const pending = Promise.resolve(rows);
  if (
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function'
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': pending.then(
            (value) =>
              new Blob([rowsToHtml(value, header)], { type: 'text/html' }),
          ),
          'text/plain': pending.then(
            (value) => new Blob([rowsToTsv(value)], { type: 'text/plain' }),
          ),
        }),
      ]);
      return;
    } catch {
      // Older Chrome takes no promise in a ClipboardItem: try plain text.
    }
  }
  await navigator.clipboard.writeText(rowsToTsv(await pending));
}

/** Saves a Blob as a download. */
export function saveBlob(blob: Blob, fileName: string) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/** CSV text with a BOM so Excel opens non-ASCII text as UTF-8. */
export function toCsv(rows: string[][], delimiter = ','): string {
  const quote = (value: string) =>
    /[",\n\r;\t]/.test(value) || value !== value.trim()
      ? `"${value.replace(/"/g, '""')}"`
      : value;
  return `\uFEFF${rows
    .map((row) => row.map((v) => quote(v ?? '')).join(delimiter))
    .join('\r\n')}\r\n`;
}
